/**
 * Service Mapping - Edit — Access `frmReportCashFlowQryServiceMapping_result_add`.
 *
 * The selected ERP service (read-only) is linked to a MISA object; Save writes
 * the chosen `misa_id` onto `service.misa_code`.
 */
import { Autocomplete, Box, Dialog, DialogContent, TextField } from '@mui/material'
import DialogTitleBar from '../../../../components/DialogTitleBar'
import { useEffect, useMemo, useState } from 'react'
import {
  useGetCashflowLookupQuery,
  useSaveCashflowMapServiceMutation,
  type MapService,
} from '../../../../store/api/reportCashflowApi'
import { useSnackbar } from '../../../../hooks/useSnackbar'
import { FORM_GRID_2COL } from '../../../../theme/responsive'
import { readonlyFieldSx } from './formHelpers'

interface Props {
  open: boolean
  row: Record<string, unknown> | null
  canEdit: boolean
  onClose: () => void
  onSaved: () => void
}

export default function ServiceMappingEditDialog({ open, row, canEdit, onClose, onSaved }: Props) {
  const snack = useSnackbar()
  const svc = (row ?? {}) as Partial<MapService>
  const [misa, setMisa] = useState<{ value: string; label: string } | null>(null)
  const [misaInput, setMisaInput] = useState('')
  const [save, { isLoading }] = useSaveCashflowMapServiceMutation()

  const { data: misaLookup } = useGetCashflowLookupQuery({ kind: 'misa-object', q: misaInput || undefined }, { skip: !open })
  const seededMisa = useMemo(
    () => (svc.misaId ? { value: String(svc.misaId), label: `${svc.misaCode ?? ''} - ${svc.misaName ?? ''}` } : null),
    [svc.misaId, svc.misaCode, svc.misaName],
  )
  const options = useMemo(() => {
    const items = misaLookup?.items ?? []
    if (seededMisa && !items.some((i) => i.value === seededMisa.value)) return [seededMisa, ...items]
    return items
  }, [misaLookup, seededMisa])

  useEffect(() => {
    if (!open) return
    setMisa(seededMisa)
    setMisaInput('')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const handleSave = async () => {
    if (!canEdit) return
    if (!svc.serviceCode) {
      window.alert('Please choose Service!')
      return
    }
    try {
      await save({ serviceCode: String(svc.serviceCode), misaId: misa?.value ?? null }).unwrap()
      snack.success('Successfully Updated')
      onSaved()
      onClose()
    } catch (err: unknown) {
      const detail = (err as { data?: { detail?: string } })?.data?.detail
      snack.error(detail ?? 'Could not save the mapping.')
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitleBar
        title="Service Mapping - Edit"
        onSave={handleSave}
        onCancel={onClose}
        saving={isLoading}
        saveDisabled={isLoading || !canEdit || !svc.serviceCode}
      />
      <DialogContent>
        <Box sx={{ ...FORM_GRID_2COL, mt: 0.5 }}>
          <TextField label="Service Code" value={svc.serviceCode ?? ''} size="small" fullWidth InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} sx={readonlyFieldSx} />
          <TextField label="Service Name" value={svc.serviceName ?? ''} size="small" fullWidth InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} sx={readonlyFieldSx} />
          <TextField label="Service TaxNo" value={svc.taxNo ?? ''} size="small" fullWidth InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} sx={readonlyFieldSx} />
          <Box />
          <Autocomplete
            size="small"
            options={options}
            getOptionLabel={(o) => o.label}
            isOptionEqualToValue={(o, v) => o.value === v.value}
            value={misa}
            onChange={(_, v) => setMisa(v)}
            inputValue={misaInput}
            onInputChange={(_, v) => setMisaInput(v)}
            sx={{ gridColumn: '1 / -1' }}
            renderInput={(p) => <TextField {...p} label="MISA" InputLabelProps={{ shrink: true }} />}
          />
        </Box>
      </DialogContent>
    </Dialog>
  )
}
